<!--
  NUOVA PRENOTAZIONE — ingresso unico, guidato dalle abilitazioni.

  Si sceglie il servizio in un posto solo, e le opzioni arrivano dalle
  capability del partner (`booking_type`, `journey_type`). Ogni servizio si
  apre qui sotto, spostamento interno compreso (`JourneyFormComponent`,
  condiviso con la rotta `/journey/:date`).

  ⚠️ I FORM PER SERVIZIO contengono la logica vera — slot, durate,
  geocodifica, pacchetti valet, notifiche, upload: questa pagina decide
  COME ci si arriva, non cosa fanno.
-->
<template>
  <div class="nuova">
    <div class="nuova__head">
      <button
        type="button"
        class="nuova__back"
        aria-label="Torna indietro"
        @click="indietro"
      >
        ‹
      </button>
      <div>
        <h1 class="nuova__title">
          Nuova prenotazione
        </h1>
        <p class="nuova__sub">
          Driver e jolly li assegna Simba dopo la conferma.
        </p>
      </div>
    </div>

    <!-- Si arriva qui da un giorno del calendario.

         ⚠️ La data NON si applica subito: il calendario dei tre servizi resta
         disabilitato finché non sono scelti cliente e sede, perché senza
         quelli il calcolo degli slot non ha i dati (e il server risponde 500).
         Si imposta da sé nell'istante in cui il campo diventa scrivibile. Qui
         lo si dice, altrimenti l'operatore vede un campo data vuoto e pensa
         che la scelta fatta in Agenda sia andata persa. -->
    <div
      v-if="dataScelta"
      class="nuova__data"
    >
      Stai creando per <strong>{{ dataSceltaEstesa }}</strong>, la data scelta
      in Agenda. Si imposta da sola appena scegli cliente e sede: da lì puoi
      cambiarla.
    </div>

    <!-- ---------- 1 · servizio ---------- -->
    <section class="nuova__card">
      <header class="nuova__step">
        <span class="nuova__n">1</span>
        <h3>Servizio</h3>
        <div class="nuova__spacer" />
        <span
          v-if="scelto"
          class="nuova__change"
          @click="scegli(null)"
        >Cambia</span>
      </header>

      <div class="nuova__body">
        <div
          v-if="!attivi.length"
          class="nuova__none"
        >
          Nessun servizio attivo sul tuo contratto. Parlane con Simba.
        </div>

        <div
          v-else
          class="nuova__opts"
        >
          <button
            v-for="s in attivi"
            :key="s.value"
            type="button"
            class="nuova__opt"
            :class="{ 'is-on': scelto === s.value }"
            @click="scegli(s.value)"
          >
            <span class="nuova__optName">{{ s.label }}</span>
            <span class="nuova__optDesc">{{ s.desc }}</span>
          </button>

          <!-- I servizi non attivi non si nascondono: si dice perché.
               Nasconderli lascia l'operatore a chiedersi se ha sbagliato
               pagina, e toglie a Simba l'occasione di venderli. -->
          <div
            v-for="s in spenti"
            :key="s.value"
            class="nuova__opt nuova__opt--off"
          >
            <span class="nuova__optName">{{ s.label }}</span>
            <span class="nuova__optDesc">Non attivo sul tuo contratto</span>
          </div>
        </div>
      </div>
    </section>

    <!-- ---------- 2 · il form del servizio scelto ---------- -->
    <section
      v-if="scelto"
      class="nuova__card"
    >
      <header class="nuova__step">
        <span class="nuova__n">2</span>
        <h3>{{ etichettaScelto }}</h3>
        <div class="nuova__spacer" />
        <!-- Clienti e sedi arrivano dalla rete e i form NON sanno caricarseli:
             se qualcosa va storto, senza questa riga a schermo resta una
             tendina vuota, indistinguibile da un partner che non ha clienti. -->
        <span
          v-if="stato.caricamento"
          class="nuova__stato"
        >Carico clienti e sedi…</span>
        <span
          v-else-if="stato.errore"
          class="nuova__stato nuova__stato--ko"
        >
          {{ stato.errore }}
          <button
            type="button"
            class="nuova__retry"
            @click="ricarica"
          >Riprova</button>
        </span>
      </header>

      <div
        v-if="stato.clientiTroncati"
        class="nuova__avviso"
      >
        L'elenco dei clienti è parziale: il server ne ha di più di quanti ne
        abbia potuti mandare. Cerca il cliente per email qui sotto invece di
        scorrere la tendina.
      </div>

      <!-- ⚠️ Pickup e Transfer si portano il proprio padding interno, quindi
           per loro il contenitore non ne mette (`--form`). Il form dello
           spostamento interno non ce l'ha: senza questa distinzione i suoi
           campi e il pulsante Salva restavano appiccicati ai bordi della
           card. -->
      <div
        class="nuova__body"
        :class="scelto === 'journey' ? 'nuova__body--journey' : 'nuova__body--form'"
      >
        <!-- La data scelta nel calendario dell'Agenda arriva ai form come
             prop: e' la pagina a conoscerla, e farla rileggere dalla query
             string anche dentro ai tre componenti significherebbe quattro
             posti che devono restare d'accordo su un nome. -->
        <!-- Notturno e pickup sono lo stesso form: cambia il listino (lo
             sceglie il server dall'ora) e sparisce l'auto di cortesia. Un
             partner ha l'uno o l'altro, mai tutti e due. -->
        <PickupComponent
          v-if="scelto === 'pickup' || scelto === 'restaurant'"
          :servizio="scelto"
          :data-iniziale="dataScelta"
          :cliente-iniziale="clienteScelto"
          :auto-iniziale="autoScelta"
        />
        <EscortComponent
          v-else-if="scelto === 'escort'"
          :data-iniziale="dataScelta"
        />
        <JourneyFormComponent
          v-else-if="scelto === 'journey'"
          :data-iniziale="dataScelta"
          @salvato="vaiInAgenda"
        />
      </div>
    </section>
  </div>
</template>

<script>
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import dayjs from 'dayjs';
import { isLoggedStore } from '../store/logged';
import PickupComponent from '../Components/PickupComponent.vue';
import EscortComponent from '../Components/EscortComponent.vue';
import JourneyFormComponent from '../Components/JourneyFormComponent.vue';
import {
  preparaNuovaPrenotazione,
  statoPrenotazione,
} from '../services/prenotazioneSetup';
// Il catalogo dei servizi e la regola su chi vede cosa stanno in
// `services/servizi.js`: li usa anche il calendario dell'Agenda, e due copie
// si allineerebbero solo a memoria.
import {
  SERVIZI,
  abilitato as servizioAbilitato,
} from '../services/servizi';

export default {
  name: 'NuovaPrenotazionePage',
  components: { PickupComponent, EscortComponent, JourneyFormComponent },

  setup() {
    const store = isLoggedStore();
    const route = useRoute();
    const router = useRouter();
    const scelto = ref(null);

    // ⚠️ QUI, nel `setup()` della pagina, e non piu' in basso: i form leggono
    // `store.customersCascader` una volta sola quando vengono creati, e se in
    // quel momento vale `null` se lo tengono per sempre. `preparaNuovaPrenotazione`
    // mette gli array nello store in modo SINCRONO prima di qualunque await,
    // quindi qualunque form montato dopo trova il contenitore giusto.
    preparaNuovaPrenotazione();

    const abilitato = (v) => servizioAbilitato(store, v);

    const attivi = computed(() => SERVIZI.filter((s) => abilitato(s.value)));
    const spenti = computed(() => SERVIZI.filter((s) => !abilitato(s.value)));

    const etichettaScelto = computed(
      () => SERVIZI.find((s) => s.value === scelto.value)?.label || ''
    );

    // La data eventualmente scelta dal calendario dell'Agenda.
    //
    // ⚠️ Si mostra, ma NON compila il calendario dentro al form: quello vive
    // in `DateSelectComponent`, che non ha una prop per impostarlo da fuori.
    // Dirlo e' meglio che far finta di niente: chi arriva qui da un giorno del
    // calendario deve sapere che la data va confermata nel form.
    const dataScelta = computed(() => {
      const d = String(route.query.data || '');
      // Un giorno passato no: lì il partner non inserisce.
      return /^\d{4}-\d{2}-\d{2}$/.test(d) && !dayjs(d).isBefore(dayjs(), 'day') ? d : '';
    });

    // Arrivo dalla scheda cliente: cliente e auto già scelti.
    const idDaQuery = (k) => {
      const v = String(route.query[k] || '');
      return /^[0-9a-f-]{36}$/i.test(v) ? v : '';
    };
    const clienteScelto = computed(() => idDaQuery('cliente'));
    const autoScelta = computed(() => idDaQuery('auto'));

    const dataSceltaEstesa = computed(() => {
      if (!dataScelta.value) return '';
      const [a, m, g] = dataScelta.value.split('-');
      return new Date(Number(a), Number(m) - 1, Number(g)).toLocaleDateString('it-IT', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
      });
    });

    // Tutti i servizi si aprono qui sotto, spostamento interno compreso.
    const scegli = (v) => {
      scelto.value = v;
    };

    // A salvataggio fatto si torna in Agenda sul giorno creato, come fa la
    // pagina dello spostamento interno.
    const vaiInAgenda = (giorno) => {
      router.push({ path: '/agenda', query: { giorno } });
    };

    // Si può arrivare qui con il servizio già deciso (es. dal calendario):
    // in quel caso il primo passo è gia' fatto. Un servizio non abilitato
    // nella query viene ignorato, non forzato.
    onMounted(() => {
      const q = route.query.servizio;
      if (q && abilitato(q)) scegli(q);
    });

    const indietro = () => router.back();

    // Riprovare deve rifare il giro intero, azzeramento compreso: un secondo
    // tentativo che si limitasse a ri-scaricare accoderebbe i clienti a quelli
    // gia' presenti, e chi ha ricaricato dopo un errore parziale se li
    // ritroverebbe doppi in tendina.
    const ricarica = () => preparaNuovaPrenotazione();

    return {
      attivi,
      spenti,
      scelto,
      etichettaScelto,
      scegli,
      vaiInAgenda,
      indietro,
      ricarica,
      dataScelta,
      dataSceltaEstesa,
      clienteScelto,
      autoScelta,
      stato: statoPrenotazione,
    };
  },
};
</script>

<style scoped>
.nuova { padding: 4px 8px 32px; text-align: left; display: flex; flex-direction: column; gap: 16px; }

.nuova__head { display: flex; align-items: flex-start; gap: 14px; }
.nuova__back { width: 30px; height: 30px; border: 1px solid var(--color-border); background: #fff; border-radius: 6px; font-size: 15px; color: var(--color-text-secondary); cursor: pointer; flex: none; }
.nuova__title { margin: 0; font-size: 23px; font-weight: 700; letter-spacing: -0.015em; }
.nuova__sub { margin: 3px 0 0; font-size: 13px; color: var(--color-text-secondary); }

.nuova__card { background: #fff; border: 1px solid var(--color-border); border-radius: 6px; box-shadow: var(--el-box-shadow-lighter); overflow: hidden; }
.nuova__step { display: flex; align-items: center; gap: 11px; padding: 13px 18px; border-bottom: 1px solid var(--color-border); }
.nuova__step h3 { margin: 0; font-size: 14.5px; font-weight: 600; }
.nuova__n { width: 23px; height: 23px; border-radius: 50%; background: #232c51; color: #fff; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 700; flex: none; }
.nuova__spacer { flex-grow: 1; }
.nuova__change { font-size: 12.5px; font-weight: 600; color: var(--color-brand); cursor: pointer; }

/* Stato del caricamento di clienti e sedi. Sta nell'intestazione del passo 2
   perche' e' li' che serve saperlo: e' il form sotto a dipenderne. */
.nuova__stato { display: inline-flex; align-items: center; gap: 8px; font-size: 12.5px; color: var(--color-text-secondary); }
.nuova__stato--ko { color: var(--state-danger); font-weight: 600; }
.nuova__retry { font: inherit; font-size: 12px; font-weight: 600; color: var(--state-danger); background: var(--state-danger-bg); border: 1px solid var(--state-danger-line); border-radius: 5px; padding: 3px 9px; cursor: pointer; }

.nuova__avviso { margin: 0; padding: 10px 18px; font-size: 12.5px; line-height: 1.5; color: var(--state-attn); background: var(--state-attn-bg); border-bottom: 1px solid var(--state-attn-line); }
.nuova__body { padding: 16px 18px; }
.nuova__body--form { padding: 0; }
/* Lo spostamento interno usa il padding standard della card. */
.nuova__body--journey { padding: 16px 18px 20px; }

/* Qui c'era la regola che toglieva bordo e ombra alla `el-card` dei form di
   servizio, per non avere una cornice dentro l'altra. I tre form non usano
   piu' `el-card`: la regola non aveva piu' niente da spogliare. */

.nuova__opts { display: grid; grid-template-columns: repeat(auto-fit, minmax(230px, 1fr)); gap: 12px; }
.nuova__opt { display: flex; flex-direction: column; align-items: flex-start; gap: 5px; text-align: left; background: #fff; border: 1px solid var(--color-border); border-radius: 6px; padding: 16px 18px; font: inherit; cursor: pointer; transition: border-color 0.15s ease; }
.nuova__opt:hover { border-color: var(--color-brand-light); }
.nuova__opt.is-on { border: 2px solid var(--color-brand); padding: 15px 17px; }
.nuova__opt--off { background: var(--color-bg-secondary); border-style: dashed; cursor: default; }
.nuova__opt--off .nuova__optName,
.nuova__opt--off .nuova__optDesc { color: var(--color-text-muted); }
.nuova__optName { font-size: 15px; font-weight: 600; }
.nuova__optDesc { font-size: 12.5px; line-height: 1.45; color: var(--color-text-secondary); }

.nuova__none { font-size: 13px; color: var(--color-text-muted); padding: 8px 0; }

/* La data arrivata dal calendario. Tono informativo — «e' andata bene» — e non
   piu' d'attenzione: da quando il form la eredita davvero, non c'e' nulla che
   l'operatore debba rimediare. */
.nuova__data { font-size: 13px; line-height: 1.5; color: var(--state-active); background: var(--state-active-bg); border: 1px solid var(--state-active-line); border-radius: 6px; padding: 10px 14px; }
</style>
